import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import * as AppActionTypes from 'store/App/types';
import { IAction, IDaySegmentGracePeriodPolicy, IOnboardingEmployeeList, IProcessOnboardingEmployeesResponse, IProcessOnboardingResponse, ISite, ISiteBalance, ISiteWithholdings } from 'types';
import { IHealthReport } from 'types/HealthReport';
import { buildOnboardingSummary } from 'utils';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import { IUpdateWithholdingsMeta } from './actions';
import { Site } from './types';

interface ISitePayload {
  siteId?: string;
}

interface IGenerateHealthReport {
  siteId: string;
  dateRange: {
    start: string;
    end: string;
  };
  onSuccess: (rules: IHealthReport[]) => void;
  onError: (error: any) => void;
}

export function* getSite({ payload }: IAction<string, ISitePayload>) {
  try {
    if (payload?.siteId) {
      // dispatch the request action to handle loading state
      yield put(Actions.getSiteRequest(payload.siteId));

      const response: ApiResponse<ISite> = yield call(API.Sites.show, payload.siteId);
      yield put(Actions.getSiteSuccess(response.body));
    } else {
      yield put(Actions.clearSite());
    }
  } catch (error: any) {
    yield put(Actions.getSiteError(error));
  }
}

export function* getSiteBalance({ payload }: IAction<Site, ISitePayload>) {
  try {
    const response: ApiResponse<ISiteBalance> = yield call(API.Sites.showSiteBalance, payload.siteId);
    yield put(Actions.getSiteBalanceSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSiteBalanceError(error));
  }
}

export function* updateGracePeriodPolicy({ payload }: IAction<Site, { siteId: string; body: IDaySegmentGracePeriodPolicy; onSuccess?: () => void }>) {
  const { siteId, body, onSuccess } = payload;
  try {
    const response: ApiResponse<ISite> = yield call(API.Sites.updateGracePeriodPolicy, siteId, body);

    yield put(Actions.updateGracePeriodPolicySuccess(response.body));
    yield call(toast, SuccessToastContainer({ message: 'Grace period updated successfully' }));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.updateGracePeriodPolicyError(formattedError));
  }
}

export function* updateBusinessDayStart({ payload }: IAction<Site, { siteId: string; businessDayStart: string; onSuccess?: () => void }>) {
  const { siteId, businessDayStart, onSuccess } = payload;
  try {
    const response: ApiResponse<ISite> = yield call(API.Sites.updateBusinessDayStart, siteId, businessDayStart);

    yield put(Actions.updateBusinessDayStartSuccess(response.body));
    yield call(toast, SuccessToastContainer({ message: 'Start of business day updated successfully' }));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.updateBusinessDayStartError(formattedError));
  }
}

export function* updateCreditCardFee({ payload }: IAction<Site, { siteId: string; tipCalculatorPercentFee: number; onSuccess?: () => void }>) {
  const { siteId, tipCalculatorPercentFee, onSuccess } = payload;
  try {
    const response: ApiResponse<ISite> = yield call(API.Sites.updateCreditCardFee, siteId, tipCalculatorPercentFee);

    yield put(Actions.updateCreditCardFeeSuccess(response.body));
    yield call(toast, SuccessToastContainer({ message: 'Credit card fee updated successfully' }));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.updateCreditCardFeeError(formattedError));
  }
}

export function* updateWithholdings({ payload, meta }: IAction<Site, { siteId: string; body: ISiteWithholdings }, IUpdateWithholdingsMeta>) {
  const { siteId, body } = payload;
  try {
    const response: ApiResponse<ISite> = yield call(API.Sites.updateSiteWithholdings, siteId, body);

    yield put(Actions.updateSiteWithholdingsSuccess(response.body));
    yield call(toast, SuccessToastContainer({ message: meta?.successMessage || 'Withholdings updated successfully' }));

    // Execute on success function
    if (meta?.onSuccess) yield call(meta.onSuccess);
  } catch (error: any) {
    yield put(Actions.updateSiteWithholdingsError(error));
  }
}

export function* generateHealthReport({ payload }: IAction<Site, IGenerateHealthReport>) {
  try {
    const generatedHealthReportResponse: ApiResponse<IHealthReport[]> = yield call(API.Sites.generateHealthReport, payload.siteId, payload.dateRange.start, payload.dateRange.end);

    yield put(Actions.generateHealthReportSuccess(generatedHealthReportResponse.body));

    yield call(payload.onSuccess, generatedHealthReportResponse.body);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield call(payload.onError, formattedError);
    yield put(Actions.generateHealthReportError(formattedError, false));
  }
}

interface IGenerateEmployeeList {
  siteId: string;
  startDate: string;
  endDate: string;
  options?: IApiQueryOptions;
  onSuccess: (employees: IOnboardingEmployeeList) => void;
  onError: (error: any) => void;
}

export function* generateEmployeeList({ payload }: IAction<Site, IGenerateEmployeeList>) {
  try {
    const generatedEmployeeListResponse: ApiResponse<IOnboardingEmployeeList> = yield call(API.Sites.getEmployeesList, payload.siteId, payload.startDate, payload.endDate, payload.options);

    yield put(Actions.generateEmployeeListSuccess(generatedEmployeeListResponse.body));

    yield call(payload.onSuccess, generatedEmployeeListResponse.body);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield call(payload.onError, formattedError);
    yield put(Actions.generateEmployeeListError(formattedError, false));
  }
}

interface IProcessOnboardingEmployees {
  siteId: string;
  body: IOnboardingEmployeeList;
  onSuccess: (response: IProcessOnboardingEmployeesResponse) => void;
  onError: (error: any) => void;
}

export function* processOnboardingEmployees({ payload }: IAction<Site, IProcessOnboardingEmployees>) {
  try {
    // Orchestration lives server-side: a single call processes every candidate and returns the
    // decorated employee list. The UI summary is derived from that response.
    const response: ApiResponse<IProcessOnboardingResponse> = yield call(API.Sites.processOnboarding, payload.siteId, payload.body);

    const summary = buildOnboardingSummary(response.body.employees);

    yield put(Actions.processOnboardingEmployeesSuccess(summary));
    yield call(payload.onSuccess, summary);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.processOnboardingEmployeesError(formattedError, true));
    yield call(payload.onError, formattedError);
  }
}

export function* watchGetSite() {
  yield takeLatest([AppActionTypes.App.SWITCH_PROFILE, AppActionTypes.App.SET_CURRENT_SITE], getSite);
}

export function* watchGetSiteBalance() {
  yield takeEvery(Site.GET_SITE_BALANCE_REQUEST, getSiteBalance);
}

export function* watchGracePeriodPolicy() {
  yield takeEvery(Site.UPDATE_GRACE_PERIOD_POLICY_REQUEST, updateGracePeriodPolicy);
}

export function* watchBusinessDayStart() {
  yield takeEvery(Site.UPDATE_BUSINESS_DAY_START_REQUEST, updateBusinessDayStart);
}

export function* watchCreditCardFee() {
  yield takeEvery(Site.UPDATE_CREDIT_CARD_FEE_REQUEST, updateCreditCardFee);
}

export function* watchUpdateWithholdings() {
  yield takeEvery(Site.UPDATE_WITHHOLDINGS_REQUEST, updateWithholdings);
}

export function* watchGenerateHealthReport() {
  yield takeEvery(Site.GENERATE_HEALTH_REPORT_REQUEST, generateHealthReport);
}

export function* watchGenerateEmployeeList() {
  yield takeEvery(Site.GENERATE_EMPLOYEE_LIST_REQUEST, generateEmployeeList);
}

export function* watchProcessOnboardingEmployees() {
  yield takeEvery(Site.PROCESS_ONBOARDING_EMPLOYEES_REQUEST, processOnboardingEmployees);
}

function* siteSaga() {
  yield all([
    fork(watchGetSite),
    fork(watchGetSiteBalance),
    fork(watchGracePeriodPolicy),
    fork(watchBusinessDayStart),
    fork(watchCreditCardFee),
    fork(watchUpdateWithholdings),
    fork(watchGenerateHealthReport),
    fork(watchGenerateEmployeeList),
    fork(watchProcessOnboardingEmployees),
  ]);
}

export { siteSaga };
